import { action } from 'mobx';
import { HubModuleType, TCreateHubModuleDTO, THubModuleDTO, } from '../../../../../../../api/event-hubs.dao';
import { AbstractFormViewModelController } from '../../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { FormVmItemConfigType, InitialData } from '../../../../../../../infrastructure/form-viewmodel-controller';

export abstract class HubModuleVM extends AbstractFormViewModelController {
    private static nextClientId = 1;

    protected savedSnapshot: string = '';
    private clientId: number = HubModuleVM.nextClientId++;
    private readonly _moduleId: number | undefined;

    protected constructor(
        moduleId: number | undefined,
        name: string,
        config: FormVmItemConfigType[],
        initial: InitialData,
    ) {
        super(initial, name, config);
        this._moduleId = moduleId;
    }

    public abstract getModuleType(): HubModuleType;
    public abstract toCreateDTO(): TCreateHubModuleDTO;
    public abstract isValid(): boolean;
    public abstract clone(): HubModuleVM;

    // eslint-disable-next-line no-empty-function
    public dispose(): void {}

    public getClientId = (): number => {
        return this.clientId;
    };

    public getModuleId = (): number | null => {
        return this._moduleId ?? null;
    };

    public getLabel = (): string => {
        return this.getStringValue('label');
    };

    @action
    public setEnabled = (enabled: boolean): void => {
        this.setValue('enabled', enabled);
    };

    public isNew = (): boolean => {
        return this._moduleId === undefined;
    };

    public isEnabled = (): boolean => {
        return this.getFormVmValuePropsByKey('enabled').value as boolean;
    };

    public hasUnsavedChanges = (): boolean => {
        return this.isNew() || this.serialize() !== this.savedSnapshot;
    };

    @action
    public markTouched = (): void => {
        this.setVmTouched();
    };

    public updatePositionOrder = (order: number): void => {
        this.setValue('positionOrder', order);
    };

    public toUpdateDTO = (): THubModuleDTO => {
        if (this._moduleId === undefined) {
            throw new Error('toUpdateDTO called on a module without a moduleId');
        }
        return {
            ...this.toCreateDTO(),
            moduleId: this._moduleId,
        };
    };

    protected serialize = (): string => {
        return JSON.stringify(this.toCreateDTO());
    };

    protected copyIdentityTo = (target: HubModuleVM): void => {
        target.clientId = this.clientId;
        target.savedSnapshot = this.savedSnapshot;
    };

    protected getStringValue = (key: string): string => {
        return this.getFormVmValuePropsByKey(key).value as string;
    };

    protected getBooleanValue = (key: string): boolean => {
        return this.getFormVmValuePropsByKey(key).value as boolean;
    };

    protected getNumberValue = (key: string): number => {
        return Number(this.getFormVmValuePropsByKey(key).value);
    };

    protected getTrimmedStringValue = (key: string): string => {
        return this.getStringValue(key).trim();
    };
}
